import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Star, Phone, Lock, Loader2, User, Mail, Gift, Car, Eye, EyeOff } from 'lucide-react';

export default function LoyaltyLoginPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showRegister, setShowRegister] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showRegPassword, setShowRegPassword] = useState(false);
  const { clientLogin, clientRegister } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('ref') || '';

  // Login form
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');

  // Register form
  const [registerName, setRegisterName] = useState('');
  const [registerPhone, setRegisterPhone] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');

  // Auto-show register if referral code present
  useEffect(() => {
    if (referralCode) setShowRegister(true);
  }, [referralCode]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    const result = await clientLogin(phone, password);
    setLoading(false);
    
    if (result.success) {
      navigate('/cliente');
    } else {
      setError(result.error);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    const result = await clientRegister(registerName, registerPhone, registerEmail, registerPassword, referralCode);
    setLoading(false);
    
    if (result.success) {
      navigate('/cliente');
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-900 via-blue-800 to-blue-900">
      {/* Header */}
      <div className="p-6">
        <div className="flex items-center justify-between max-w-lg mx-auto">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_carwash-quick-pos/artifacts/tady4vql_image.png" 
              alt="WD Logo" 
              className="w-10 h-10 object-contain" 
            />
            <span className="text-xl font-bold text-white">WD Lavado</span>
          </div>
          <Link 
            to="/agendar"
            className="text-sm text-blue-200 hover:text-white"
          >
            Agendar cita
          </Link>
        </div>
      </div>

      {/* Hero */}
      <div className="text-center px-4 py-8">
        <div className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-300 px-4 py-2 rounded-full mb-4">
          <Star className="w-5 h-5" />
          <span className="font-medium">Programa de Lealtad</span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
          Gana puntos por cada visita
        </h1>
        <p className="text-xl text-blue-200 max-w-md mx-auto">
          1 peso = 1 punto. Canjéalos por servicios gratis y descuentos exclusivos.
        </p>
      </div>

      {/* Benefits */}
      <div className="max-w-lg mx-auto px-4 mb-8">
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
            <Gift className="w-8 h-8 text-amber-400 mx-auto mb-2" />
            <div className="text-sm text-white font-medium">Recompensas</div>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
            <Car className="w-8 h-8 text-blue-400 mx-auto mb-2" />
            <div className="text-sm text-white font-medium">Historial</div>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
            <Star className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
            <div className="text-sm text-white font-medium">Puntos</div>
          </div>
        </div>
      </div>

      {/* Login/Register Form */}
      <div className="max-w-md mx-auto px-4 pb-12">
        <div className="card-base p-6 animate-fadeIn">
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-200 text-red-700 rounded-lg text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          {!showRegister ? (
            <>
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-slate-900">Accede a tu cuenta</h2>
                <p className="text-slate-600 text-sm mt-1">Ingresa con tu número de teléfono</p>
              </div>
              
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="phone">Teléfono</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="5551234567"
                      className="pl-10 h-12"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      required
                      data-testid="client-phone-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="password">Contraseña</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      className="pl-10 pr-10 h-12"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      data-testid="client-password-input"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                      onClick={() => setShowPassword(!showPassword)}
                      data-testid="toggle-client-password"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full h-14 text-lg bg-blue-600 hover:bg-blue-700"
                  disabled={loading}
                  data-testid="client-login-btn"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Entrar'}
                </Button>
              </form>
              
              <div className="mt-6 text-center">
                <p className="text-slate-600 text-sm">¿No tienes cuenta?</p>
                <button
                  type="button"
                  className="text-blue-600 hover:underline font-medium mt-1"
                  onClick={() => setShowRegister(true)}
                  data-testid="show-register-btn"
                >
                  Regístrate gratis
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-slate-900">Crear cuenta</h2>
                <p className="text-slate-600 text-sm mt-1">Únete al programa de lealtad</p>
              </div>

              {referralCode && (
                <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-3" data-testid="referral-banner">
                  <Gift className="w-5 h-5 text-emerald-600 shrink-0" />
                  <div>
                    <div className="text-sm font-medium text-emerald-800">Te invitaron con el código: {referralCode}</div>
                    <div className="text-xs text-emerald-600">Recibirás 50 puntos de regalo al registrarte</div>
                  </div>
                </div>
              )}
              
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <Label htmlFor="register-name">Nombre completo</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="register-name"
                      type="text"
                      placeholder="Juan Pérez"
                      className="pl-10 h-12"
                      value={registerName}
                      onChange={(e) => setRegisterName(e.target.value)}
                      required
                      data-testid="register-name-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="register-phone">Teléfono</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="register-phone"
                      type="tel"
                      placeholder="5551234567"
                      className="pl-10 h-12"
                      value={registerPhone}
                      onChange={(e) => setRegisterPhone(e.target.value)}
                      required
                      data-testid="register-phone-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="register-email">Correo (opcional)</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="register-email"
                      type="email"
                      placeholder="correo@ejemplo.com"
                      className="pl-10 h-12"
                      value={registerEmail}
                      onChange={(e) => setRegisterEmail(e.target.value)}
                      data-testid="register-email-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="register-password">Contraseña</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="register-password"
                      type={showRegPassword ? "text" : "password"}
                      placeholder="••••••••"
                      className="pl-10 pr-10 h-12"
                      value={registerPassword}
                      onChange={(e) => setRegisterPassword(e.target.value)}
                      required
                      data-testid="register-password-input"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                      onClick={() => setShowRegPassword(!showRegPassword)}
                      data-testid="toggle-register-password"
                    >
                      {showRegPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full h-14 text-lg bg-blue-600 hover:bg-blue-700"
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Crear Cuenta'}
                </Button>
              </form>
              
              <div className="mt-6 text-center">
                <button
                  type="button"
                  className="text-blue-600 hover:underline font-medium"
                  onClick={() => setShowRegister(false)}
                  data-testid="show-login-btn"
                >
                  ¿Ya tienes cuenta? Inicia sesión
                </button>
              </div>
            </>
          )}
        </div>

        {/* Staff login link */}
        <div className="mt-6 text-center">
          <Link 
            to="/login" 
            className="text-blue-300 hover:text-white text-sm"
            data-testid="staff-login-link"
          >
            Acceso para personal →
          </Link>
        </div>
      </div>
    </div>
  );
}
